import { TonConnect } from '@tonconnect/sdk';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import * as Types from '@Global/apollo/types';
import { Viewer } from '@Global/apollo/types';
import { GuestAccountID } from '@Global/constants';
import { AppContextProvider } from './appContext';
import { BattleContextsProvider } from './battlesContext';

type ViewerContext = {
  viewer?: Viewer;
  isGuest: boolean;
  key?: string;
  updateViewer: (newViewer?: Viewer, key?: string) => void;
};

type UserFilterContext = {
  values: {
    teams?: string[];
    positions?: Types.PlayerPosition[];
    price?: {
      min?: number;
      max?: number;
    };
  } & Record<string, any>;
  setUserFilter: Dispatch<SetStateAction<Record<string, any>>>;
};

type CardsFilter = {
  sortOptions?: {
    key: 'L5' | 'L15';
    value: 'ASC' | 'DSC';
  };
  capFilterEnabled?: boolean;
  avgScoreLast15Min?: number;
  avgScoreLast15Max?: number;
  nftFilterEnabled?: boolean;
  lineupFilter?: 'confirmed' | 'expected' | null;
} & Record<string, any>;

type FilterContext = {
  cards: CardsFilter;
  setCardsFilter: Dispatch<SetStateAction<CardsFilter>>;
};

const viewerStore: ViewerContext = {
  viewer: undefined,
  isGuest: true,
  key: undefined,
  updateViewer: () => undefined,
};

const userFilterStore: UserFilterContext = {
  values: {},
  setUserFilter: () => {},
};

const filterStore: FilterContext = {
  cards: {},
  setCardsFilter: () => {},
};

export const FilterContext = React.createContext(filterStore);
export const UserFilterContext = React.createContext(userFilterStore);
export const ViewerContext = React.createContext(viewerStore);

const tonConnectManifestUrl = process.env.REACT_APP_TON_CONNECT_MANIFEST_URL;
const tonConnectWalletsListUrl = process.env.REACT_APP_TON_CONNECT_WALLETS_LIST_URL;
const tonConnectConnector = tonConnectWalletsListUrl
  ? new TonConnect({
      manifestUrl: tonConnectManifestUrl,
      walletsListSource: tonConnectWalletsListUrl,
    })
  : undefined;

/** прослойка контекста фильтрации */
export const ContextInterLayer: React.FC<React.PropsWithChildren> = ({ children }: React.PropsWithChildren) => {
  const [viewer, setViewer] = useState<Viewer | undefined>(undefined);
  const [viewerKey, setViewerKey] = useState<string | undefined>(undefined);
  const [cards, setCardsFilter] = useState<CardsFilter>({});
  const [userFilter, setUserFilter] = useState({});

  const isGuestViewer = useMemo(() => !viewer || viewer.id === GuestAccountID, [viewer]);

  const onUpdateViewer = useCallback((newViewer?: Viewer, key?: string) => {
    setViewer(() => (newViewer ? { ...newViewer } : undefined));
    setViewerKey(() => key);
  }, []);

  const onFilterChange = setCardsFilter;

  const onUserFilterChange = setUserFilter;

  return (
    <AppContextProvider>
      <TonConnectUIProvider
        {...(tonConnectConnector ? { connector: tonConnectConnector } : { manifestUrl: tonConnectManifestUrl })}
      >
        <ViewerContext.Provider
          value={{ viewer, key: viewerKey, isGuest: isGuestViewer, updateViewer: onUpdateViewer }}
        >
          <FilterContext.Provider value={{ cards, setCardsFilter: onFilterChange }}>
            <UserFilterContext.Provider value={{ values: userFilter, setUserFilter: onUserFilterChange }}>
              <BattleContextsProvider>{children}</BattleContextsProvider>
            </UserFilterContext.Provider>
          </FilterContext.Provider>
        </ViewerContext.Provider>
      </TonConnectUIProvider>
    </AppContextProvider>
  );
};
